import type { CurrentInventoryDetailRow } from '../types';
import { formatDateTime, formatTicketNumberForCsv } from './index';
import { toNumericTotal } from './currentInventoryViewUtils';

type GameInventorySummaryRow = {
  gameId: string;
  gameName: string;
  rate: number | null;
  packCount: number;
  totalTickets: number;
  totalValue: number;
};

type TicketPriceSummaryRow = {
  rate: number | null;
  packCount: number;
  totalTickets: number;
  totalValue: number;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCsvValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const formatCsvRow = (values: Array<string | number | null | undefined>) =>
  values.map(formatCsvValue).join(',');

const normalizeRate = (rate: number | null) => {
  if (rate === null) {
    return null;
  }

  const parsedRate = Number(rate);
  return Number.isFinite(parsedRate) ? parsedRate : null;
};

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatRate = (rate: number | null) => (
  rate === null ? '' : formatCurrency(rate)
);

const compareRates = (left: number | null, right: number | null) => {
  if (left === right) {
    return 0;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return left - right;
};

const compareGameIds = (left: string, right: string) => {
  const leftNumber = Number(left);
  const rightNumber = Number(right);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber !== rightNumber) {
    return leftNumber - rightNumber;
  }

  return left.localeCompare(right);
};

const getRateKey = (rate: number | null) => rate === null ? 'unpriced' : String(rate);

const buildGameInventorySummary = (
  rows: CurrentInventoryDetailRow[]
): GameInventorySummaryRow[] => {
  const summaries = new Map<string, GameInventorySummaryRow>();

  rows.forEach((row) => {
    const rate = normalizeRate(row.rate);
    const key = `${row.game_id}::${getRateKey(rate)}`;
    const current = summaries.get(key) ?? {
      gameId: row.game_id,
      gameName: row.game_name || '',
      rate,
      packCount: 0,
      totalTickets: 0,
      totalValue: 0,
    };

    current.packCount += 1;
    current.totalTickets += toNumericTotal(row.remaining_tickets);
    current.totalValue += toNumericTotal(row.remaining_value);

    if (!current.gameName && row.game_name) {
      current.gameName = row.game_name;
    }

    summaries.set(key, current);
  });

  return Array.from(summaries.values()).sort((left, right) => (
    compareRates(left.rate, right.rate)
    || compareGameIds(left.gameId, right.gameId)
    || left.gameName.localeCompare(right.gameName)
  ));
};

const buildTicketPriceSummary = (
  rows: GameInventorySummaryRow[]
): TicketPriceSummaryRow[] => {
  const summaries = new Map<string, TicketPriceSummaryRow>();

  rows.forEach((row) => {
    const key = getRateKey(row.rate);
    const current = summaries.get(key) ?? {
      rate: row.rate,
      packCount: 0,
      totalTickets: 0,
      totalValue: 0,
    };

    current.packCount += row.packCount;
    current.totalTickets += row.totalTickets;
    current.totalValue += row.totalValue;
    summaries.set(key, current);
  });

  return Array.from(summaries.values()).sort((left, right) => compareRates(left.rate, right.rate));
};

const buildInventorySummaryCsvLines = (rows: CurrentInventoryDetailRow[]) => {
  const gameSummary = buildGameInventorySummary(rows);
  const ticketPriceSummary = buildTicketPriceSummary(gameSummary);
  const priceSummaryByRate = new Map(
    ticketPriceSummary.map((row) => [getRateKey(row.rate), row])
  );
  const gameSummaryLines = [
    'Game Summary',
    formatCsvRow([
      'Game ID',
      'Game',
      'Pack Count',
      'Total Tickets',
      'Rate per Ticket',
      'Total Value',
    ]),
  ];

  gameSummary.forEach((row, index) => {
    gameSummaryLines.push(formatCsvRow([
      row.gameId,
      row.gameName,
      row.packCount,
      row.totalTickets,
      formatRate(row.rate),
      formatCurrency(row.totalValue),
    ]));

    const nextRow = gameSummary[index + 1];
    if (!nextRow || nextRow.rate !== row.rate) {
      const priceSummary = priceSummaryByRate.get(getRateKey(row.rate));
      if (priceSummary) {
        const subtotalLabel = row.rate === null
          ? 'Unpriced Subtotal'
          : `${formatCurrency(row.rate)} Subtotal`;
        gameSummaryLines.push(formatCsvRow([
          subtotalLabel,
          '',
          priceSummary.packCount,
          priceSummary.totalTickets,
          formatRate(priceSummary.rate),
          formatCurrency(priceSummary.totalValue),
        ]));
      }
    }
  });

  const grandTotal = ticketPriceSummary.reduce(
    (total, row) => ({
      packCount: total.packCount + row.packCount,
      totalTickets: total.totalTickets + row.totalTickets,
      totalValue: total.totalValue + row.totalValue,
    }),
    { packCount: 0, totalTickets: 0, totalValue: 0 }
  );

  const ticketSummaryLines = [
    'Ticket Summary',
    formatCsvRow(['$ Value', 'Packs', 'Tickets', 'Total Value']),
    ...ticketPriceSummary.map((row) => formatCsvRow([
      formatRate(row.rate),
      row.packCount,
      row.totalTickets,
      formatCurrency(row.totalValue),
    ])),
    formatCsvRow([
      'TOTAL',
      grandTotal.packCount,
      grandTotal.totalTickets,
      formatCurrency(grandTotal.totalValue),
    ]),
  ];

  return [
    '',
    ...gameSummaryLines,
    '',
    ...ticketSummaryLines,
  ];
};

export const buildCurrentInventoryCsv = (
  rows: CurrentInventoryDetailRow[],
  storeTimezone?: string | null
) => {
  const currentTimeZone =
    storeTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers = [
    'Game ID',
    'Game Name',
    'Pack ID',
    'Status',
    'Current Location',
    'Lottery Status Date',
    'Date Activated',
    'Completed Date',
    'Completed Type',
    'Tickets Per Pack',
    'Rate',
    'Current Ticket',
    'Remaining Tickets',
    'Remaining Value',
    'Returned Tickets',
    'Pack Location',
    'Backstock State',
  ];

  const csvRows = rows.map((row) => [
    row.game_id,
    row.game_name,
    row.pack_id,
    row.status,
    row.subgroup,
    formatDateTime(row.lottery_status_date, currentTimeZone),
    formatDateTime(row.activated_date, currentTimeZone),
    formatDateTime(row.completed_date, currentTimeZone),
    row.completed_type,
    row.tickets_per_pack,
    row.rate,
    formatTicketNumberForCsv(row.latest_audit_ticket_number),
    row.remaining_tickets,
    row.remaining_value,
    row.returned_tickets,
    row.pack_location,
    row.backstock_state,
  ]);

  return [
    formatCsvRow(headers),
    ...csvRows.map(formatCsvRow),
    ...buildInventorySummaryCsvLines(rows),
  ].join('\n');
};

export const normalizePackIdForApi = (packId: string) => {
  const digits = String(packId).replace(/\D/g, '');
  return digits || packId;
};
